import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Input, Button, Typography } from '../../../Material-UI/Components';
import APIService from '../../../service/APIService';
import { useToast, ToastConstants, useLoader } from '../../../store/context';
import AuthShell from '../AuthShell';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  fieldWrap: {
    marginTop: 8,
  },
  input: {
    width: '100%',
    margin: '10px 0 !important',
    '& .MuiOutlinedInput-root': {
      borderRadius: 18,
      background: '#f8fbff',
      minHeight: 56,
    },
  },
  noteCard: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f0f9ff',
    padding: '14px 16px',
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0ea5e9',
  },
  noteText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#334155',
  },
  sectionLabel: {
    marginTop: 18,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  },
  helperRow: {
    marginTop: 14,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  helperLink: {
    color: '#0369a1',
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  primaryAction: {
    marginTop: '18px !important',
    minHeight: 52,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
    boxShadow: '0 16px 28px rgba(14, 165, 233, 0.28)',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.08em',
    '&:hover': {
      background: 'linear-gradient(135deg, #075985 0%, #0284c7 100%)',
    },
  },
}));

const PasswordResetForm = ({ email }) => {
  const classes = useStyles();
  const history = useHistory();
  const showToast = useToast();
  const showLoader = useLoader();
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleOTPSubmit = (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      showToast('Password mismatch', ToastConstants.ERROR);
      return;
    }

    showLoader(true);
    APIService.verifyOTP(email, otp, password)
      .then(() => history.push('/login'))
      .catch((err) => showToast(err.response.data.message, ToastConstants.ERROR))
      .finally(() => showLoader(false));
  };

  return (
    <form className={classes.form} onSubmit={handleOTPSubmit}>
      <div className={classes.fieldWrap}>
        <Input
          className={classes.input}
          type="password"
          label="Verification code"
          onChange={(event) => setOtp(event.target.value)}
          value={otp}
          required
          fullWidth
        />
      </div>
      <div className={classes.fieldWrap}>
        <Input
          className={classes.input}
          type="password"
          label="New password"
          onChange={(event) => setPassword(event.target.value)}
          value={password}
          required
          fullWidth
        />
      </div>
      <div className={classes.fieldWrap}>
        <Input
          className={classes.input}
          type="password"
          label="Confirm new password"
          onChange={(event) => setConfirmPassword(event.target.value)}
          value={confirmPassword}
          required
          fullWidth
        />
      </div>

      <div className={classes.noteCard}>
        <div className={classes.noteLabel}>Step 2</div>
        <div className={classes.noteText}>
          Enter the verification code from your email and choose a new password for your workspace.
        </div>
      </div>

      <Button name="Update password" type="submit" className={classes.primaryAction} fullWidth size="large">
        Update password
      </Button>
    </form>
  );
};

const ForgotPassword = () => {
  const classes = useStyles();
  const history = useHistory();
  const showToast = useToast();
  const showLoader = useLoader();
  const [email, setEmail] = useState('');
  const [otpGenerated, setOtpGenerated] = useState(false);

  const handleOTPGenerate = (event) => {
    event.preventDefault();
    showLoader(true);
    APIService.generateOTP(email)
      .then(() => {
        setOtpGenerated(true);
        showToast('Verification code sent', ToastConstants.SUCCESS);
      })
      .catch(() => showToast('Email not registered', ToastConstants.ERROR))
      .finally(() => showLoader(false));
  };

  const GenerateOTPForm = () => (
    <form className={classes.form} onSubmit={handleOTPGenerate}>
      <div className={classes.fieldWrap}>
        <Input
          className={classes.input}
          type="text"
          label="Email"
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          autoFocus
          required
          fullWidth
        />
      </div>

      <div className={classes.noteCard}>
        <div className={classes.noteLabel}>Step 1</div>
        <div className={classes.noteText}>
          We will send a verification code to your email so you can securely reset your password.
        </div>
      </div>

      <div className={classes.sectionLabel}>Account recovery</div>

      <Button name="Send OTP" type="submit" className={classes.primaryAction} fullWidth size="large">
        Send verification code
      </Button>

      <div className={classes.helperRow}>
        <Typography variant="body2" color="textSecondary">
          Changed your mind?
        </Typography>
        <span onClick={() => history.push('/login')} className={classes.helperLink}>
          Return to login
        </span>
      </div>
    </form>
  );

  return (
    <AuthShell
      pageTitle="Reset Password | MyJobAide"
      canonicalPath="/track/forgot"
      robotsContent="noindex,follow"
      eyebrow="Career Operating System"
      heroTitle="Recover your workspace without losing momentum."
      heroSubtitle="Reset your password and return to your saved strategy, execution context, and interview preparation."
      heroBadges={[
        {
          label: 'Secure recovery',
          value: 'Verification-first reset',
          hint: 'Use the email tied to your workspace and complete the code check to continue safely.',
        },
        {
          label: 'Return to flow',
          value: 'Resume from your latest state',
          hint: 'Once access is restored, your profile and current guidance remain connected.',
        },
      ]}
      heroQuoteTitle="Why this matters"
      heroQuoteBody="MyJobAide works best when your direction, blockers, and next steps remain connected. Password recovery should get you back to that shared context quickly."
      formEyebrow="Password recovery"
      formTitle={otpGenerated ? 'Verify your code' : 'Reset your password'}
      formSubtitle={
        otpGenerated
          ? `We sent a verification code to ${email || 'your email'}. Enter it below to choose a new password.`
          : 'Start the recovery flow to regain access to your MyJobAide workspace.'
      }
      footerPrompt="Remembered your password?"
      footerActionLabel="Back to sign in"
      onFooterAction={() => history.push('/login')}
    >
      {otpGenerated ? <PasswordResetForm email={email} /> : <GenerateOTPForm />}
    </AuthShell>
  );
};

export default ForgotPassword;
